"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Children, Fragment, cloneElement, createElement, isValidElement } from "react";
import { cx } from "../internal/cx.js";
function box(as, base, { gap, align, className, ...rest }, data = {}) {
    const attrs = { ...rest, className: cx(base, className), "data-gap": gap, "data-align": align };
    for (const [key, value] of Object.entries(data))
        if (value !== undefined)
            attrs[key] = value;
    return createElement(as, attrs);
}
/** Children one under another with one gap from the scale (default 2 = 8 px). Left edges line up. */
export function Stack({ as = "div", gap = 2, justify, divided, ...rest }) {
    return box(as, "jui-vstack", { gap, ...rest }, { "data-justify": justify, "data-divided": divided ? "" : undefined });
}
/**
 * Children in a row, centred on one line (default) or on the text baseline (`align="baseline"`), one gap from the scale.
 * The row for controls next to each other: buttons, a field and its button, chips. `justify="between"` pushes the last group right.
 */
export function Inline({ as = "div", gap = 2, align = "center", justify, wrap = true, ...rest }) {
    return box(as, "jui-hstack", { gap, align, ...rest }, { "data-justify": justify, "data-wrap": wrap ? undefined : "false" });
}
/** Cards, swatches, fields in columns: equal tracks, one gap. */
export function Grid({ as = "div", gap = 3, columns, min, style, ...rest }) {
    const vars = {
        ...(columns ? { "--jui-grid-cols": `repeat(${columns}, minmax(0, 1fr))` } : {}),
        ...(min !== undefined ? { "--jui-grid-min": typeof min === "number" ? `${min}px` : min } : {}),
        ...style,
    };
    return box(as, "jui-grid", { gap, style: vars, ...rest });
}
/** Takes the free space of an Inline (a search field that stretches, a title before actions). */
export function Grow({ children, className }) {
    return _jsx("div", { className: cx("jui-grow", className), children: children });
}
/** A settings form: rows with one label column, so labels, controls and actions line up on two vertical edges. */
export function Form({ children, labelWidth, onSubmit, className, style, ...rest }) {
    const vars = { ...(labelWidth ? { "--jui-form-label": `${labelWidth}px` } : {}), ...style };
    const props = { className: cx("jui-form", className), style: vars, ...rest };
    return onSubmit ? _jsx("form", { ...props, onSubmit: onSubmit, children: children }) : _jsx("div", { ...props, children: children });
}
/** One row of a Form: label column + controls (laid out as an Inline) + an optional message under them. */
export function FormRow({ label, htmlFor, icon, hint, error, align, size = "md", children, className }) {
    const labelBody = _jsxs(_Fragment, { children: [icon, label] });
    return _jsxs("div", { className: cx("jui-form-row", size === "sm" && "jui-form-row--sm", className), "data-align": align === "top" ? "top" : undefined, children: [label === undefined ? _jsx("span", { "aria-hidden": "true" })
                : htmlFor ? _jsx("label", { className: "jui-form-row-label", htmlFor: htmlFor, children: labelBody })
                    : _jsx("span", { className: "jui-form-row-label", children: labelBody }), _jsx("div", { className: "jui-form-row-control", children: children }), error ? _jsx("span", { className: "jui-form-row-msg jui-form-row-msg--error", role: "alert", children: error })
                : hint ? _jsx("span", { className: "jui-form-row-msg", children: hint }) : null] });
}
/** The buttons of a Form, under the controls (aligned with the control column). */
const PRIMARY = new Set(["primary", "filled"]);
/**
 * Von Restorff (facts model ux_laws, П333): one main button per block of actions — the first `primary`/`filled` stays, the
 * next ones are drawn tonal.
 */
export function onePrimary(children) {
    let seen = false;
    const walk = (nodes) => Children.map(nodes, child => {
        // A fragment of buttons (`actions={<>…</>}`) is the same block: look inside it.
        if (isValidElement(child) && child.type === Fragment)
            return cloneElement(child, undefined, walk(child.props.children));
        if (!isValidElement(child) || !PRIMARY.has(child.props.variant ?? ""))
            return child;
        if (!seen) {
            seen = true;
            return child;
        }
        return cloneElement(child, { variant: "tonal" });
    });
    return walk(children);
}
export function FormActions({ children, className }) {
    return _jsx(FormRow, { className: className, children: onePrimary(children) });
}
/** The page column: all the width it is given, padded by the page gutter token (24 px, 16 px under 640 px); `maxWidth`
 *  caps and centres it for a reading page. */
export function Container({ as = "div", maxWidth, className, style, ...rest }) {
    const vars = { ...(maxWidth ? { "--jui-container-max": `${maxWidth}px` } : {}), ...style };
    return createElement(as, { ...rest, className: cx("jui-container", className), style: vars });
}
/** Numbered steps of a flow on one line: done ones get a ✓, the current one is bold; a line joins neighbours. */
export function Steps({ steps, current, label, className }) {
    return _jsx("ol", { className: cx("jui-steps", className), "aria-label": label, children: steps.map((step, index) => {
            const state = index < current ? "done" : index === current ? "current" : "todo";
            return _jsxs("li", { className: "jui-step", "data-state": state, "aria-current": state === "current" ? "step" : undefined, children: [_jsx("span", { className: "jui-step-num", "aria-hidden": "true", children: state === "done" ? "✓" : index + 1 }), step] }, index);
        }) });
}
/** Read-only facts «term — value»: one term column, rows divided by a line. Children are `DescItem`. */
export function DescList({ children, labelWidth, align, framed, className, style, ...rest }) {
    const vars = { ...(labelWidth ? { "--jui-desc-label": `${labelWidth}px` } : {}), ...style };
    return _jsx("dl", { ...rest, className: cx("jui-desc", framed && "jui-desc--framed", className), style: vars, "data-align": align === "center" ? "center" : undefined, children: children });
}
/** One row of a DescList; extra attributes (data-pii, className) go to the value. */
export function DescItem({ label, icon, children, className, ...valueProps }) {
    return _jsxs("div", { className: "jui-desc-row", children: [_jsxs("dt", { className: "jui-desc-term", children: [icon, label] }), _jsx("dd", { ...valueProps, className: cx("jui-desc-value", className), children: children })] });
}
